import React, { useState, useEffect, useCallback } from 'react';

/**
 * LetterEaterAdmin
 * ────────────────
 * Admin panel for the Letter Eater game (Pac-Man-style maze where a character
 * eats letters to spell Darija sentences).
 *
 * Three sections:
 *   1. Settings — visibility, premium, name, icon, description, default script,
 *                 default character (6 options), default theme (13 options)
 *   2. Sentence picker — search Translation_DB or "🎲 Suggest 30 random" + click
 *      to add. Translations are COPIED into LetterEater_DB on add so the game
 *      stays independent of Translation_DB.
 *   3. Selected sentences — list with edit/delete (with confirmation modals)
 *
 * Endpoints: /api/admin/letter-eater/*  (all require admin token)
 *
 * Helpers (TextField, Toggle, etc.) live at MODULE level so inputs keep focus
 * while typing (avoids the React re-render unmount bug).
 */

// ──────────────────────────────────────────────────────────────────────────
// Stable helper components (module-level)
// ──────────────────────────────────────────────────────────────────────────

const CHARACTERS = [
  { value: 'blob',    label: '🩷 Blob' },
  { value: 'cat',     label: '🐱 Cat' },
  { value: 'robot',   label: '🤖 Robot' },
  { value: 'alien',   label: '👽 Alien' },
  { value: 'pumpkin', label: '🎃 Pumpkin' },
  { value: 'frog',    label: '🐸 Frog' },
];

const THEMES = [
  'White', 'Dark', 'Mint', 'Sunset', 'Ocean', 'Sahara',
  'Blossom', 'Tropical', 'Lavender', 'Candy', 'Frost', 'Coral', 'Honey',
];

const fieldClasses = (isDarkMode) => ({
  cardCls: `rounded-xl border p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`,
  labelCls: `block text-xs font-semibold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`,
  inputCls: `w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`,
  sectionTitleCls: `text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`,
  smallBtnCls: `px-3 py-1.5 rounded-lg text-xs font-semibold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'}`,
  dangerBtnCls: `px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 border border-red-500/20`,
  primaryBtnCls: `px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow hover:opacity-95`,
});

const Toggle = ({ k, label, hint, value, onChange, isDarkMode }) => (
  <label className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
    <div className="min-w-0">
      <span className="text-sm font-medium">{label}</span>
      {hint && <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{hint}</div>}
    </div>
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(k, e.target.checked)} className="w-4 h-4 flex-shrink-0 ml-3" />
  </label>
);

const TextField = ({ k, label, value, onChange, isDarkMode, type = 'text', placeholder }) => {
  const { labelCls, inputCls } = fieldClasses(isDarkMode);
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <input type={type} className={inputCls} value={value ?? ''} onChange={(e) => onChange(k, e.target.value)} placeholder={placeholder} />
    </div>
  );
};

const RadioGroup = ({ k, label, options, value, onChange, isDarkMode }) => {
  const { labelCls } = fieldClasses(isDarkMode);
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <div className="flex gap-2">
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(k, opt.value)}
            className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-all ${
              value === opt.value
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white border-transparent'
                : isDarkMode
                  ? 'bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
};

const SelectField = ({ k, label, options, value, onChange, isDarkMode }) => {
  const { labelCls, inputCls } = fieldClasses(isDarkMode);
  return (
    <div>
      <label className={labelCls}>{label}</label>
      <select className={inputCls} value={value ?? ''} onChange={(e) => onChange(k, e.target.value)}>
        {options.map(opt => (
          <option key={typeof opt === 'string' ? opt : opt.value} value={typeof opt === 'string' ? opt : opt.value}>
            {typeof opt === 'string' ? opt : opt.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────────────────────

const LetterEaterAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [settings, setSettings] = useState(null);
  const [sentences, setSentences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Picker state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);

  // Edit + delete modals
  const [editSentence, setEditSentence] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { cardCls, labelCls, inputCls, sectionTitleCls, smallBtnCls, dangerBtnCls, primaryBtnCls } = fieldClasses(isDarkMode);

  const authHeaders = { Authorization: `Bearer ${adminToken}` };

  // ─── Load ────────────────────────────────────────────────────────────
  const loadSettings = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/letter-eater/game-settings`, { headers: authHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setSettings(await r.json());
    } catch (e) {
      showNotification?.(`Failed to load settings: ${e.message}`, 'error');
    }
  }, [API, adminToken, showNotification]);

  const loadSentences = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/letter-eater/sentences`, { headers: authHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setSentences(d.sentences || []);
    } catch (e) {
      showNotification?.(`Failed to load sentences: ${e.message}`, 'error');
    }
  }, [API, adminToken, showNotification]);

  useEffect(() => {
    Promise.all([loadSettings(), loadSentences()]).finally(() => setLoading(false));
  }, [loadSettings, loadSentences]);

  // ─── Settings save ───────────────────────────────────────────────────
  const updateSetting = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const saveSettings = async () => {
    try {
      setSaving(true);
      const r = await fetch(`${API}/admin/letter-eater/game-settings`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (!r.ok) throw new Error(await r.text());
      const fresh = await r.json();
      setSettings(fresh);
      showNotification?.('Settings saved', 'success');
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Sentence picker ─────────────────────────────────────────────────
  const doSearch = async (query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    try {
      setSearching(true);
      const r = await fetch(`${API}/admin/letter-eater/search-translations?q=${encodeURIComponent(query)}&limit=30`, { headers: authHeaders });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setSearchResults(d.results || []);
    } catch (e) {
      showNotification?.(`Search failed: ${e.message}`, 'error');
    } finally {
      setSearching(false);
    }
  };

  // Debounce search
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const id = setTimeout(() => doSearch(searchQuery), 300);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const suggestRandom = async () => {
    try {
      setSearching(true);
      const r = await fetch(`${API}/admin/letter-eater/suggest-random`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setSearchResults(d.results || []);
      setSearchQuery('');
      showNotification?.(`Showing ${d.count} random suggestions`, 'success');
    } catch (e) {
      showNotification?.(`Suggest failed: ${e.message}`, 'error');
    } finally {
      setSearching(false);
    }
  };

  // Set of translation IDs already added — used for the duplicate guard
  const addedIds = React.useMemo(
    () => new Set(sentences.map((s) => s.source_translation_id).filter(Boolean)),
    [sentences]
  );

  const addSentence = async (translation_id) => {
    if (addedIds.has(translation_id)) {
      const dup = sentences.find((s) => s.source_translation_id === translation_id);
      showNotification?.(`Already added: "${dup?.english || 'this sentence'}"`, 'error');
      return;
    }
    try {
      setAddingId(translation_id);
      const r = await fetch(`${API}/admin/letter-eater/sentences`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ translation_id }),
      });
      if (!r.ok) throw new Error(await r.text());
      setSearchResults((r) => r.filter((w) => w.id !== translation_id));
      await loadSentences();
      showNotification?.('Sentence added to game', 'success');
    } catch (e) {
      showNotification?.(`Add failed: ${e.message}`, 'error');
    } finally {
      setAddingId(null);
    }
  };

  // ─── Edit + Delete ───────────────────────────────────────────────────
  const startEdit = (sentence) => setEditSentence({ ...sentence });
  const cancelEdit = () => setEditSentence(null);
  const saveEdit = async () => {
    try {
      const r = await fetch(`${API}/admin/letter-eater/sentences/${editSentence.id}`, {
        method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          english: editSentence.english,
          latin: editSentence.latin,
          arabic: editSentence.arabic,
        }),
      });
      if (!r.ok) throw new Error(await r.text());
      await loadSentences();
      setEditSentence(null);
      showNotification?.('Sentence updated', 'success');
    } catch (e) {
      showNotification?.(`Update failed: ${e.message}`, 'error');
    }
  };

  const askDelete = (sentence) => setConfirmDelete(sentence);
  const performDelete = async () => {
    try {
      const r = await fetch(`${API}/admin/letter-eater/sentences/${confirmDelete.id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!r.ok) throw new Error(await r.text());
      await loadSentences();
      setConfirmDelete(null);
      showNotification?.('Sentence deleted', 'success');
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, 'error');
    }
  };

  if (loading || !settings) {
    return <div className={`p-8 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading Letter Eater settings…</div>;
  }

  return (
    <div className="space-y-6 max-w-5xl">

      {/* Sticky header with Save */}
      <div className="flex items-center justify-between sticky top-0 z-10 py-3 -mx-6 px-6 backdrop-blur" style={{ background: isDarkMode ? 'rgba(17,24,39,0.85)' : 'rgba(255,255,255,0.85)' }}>
        <div>
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>👾 Letter Eater</h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Pick sentences from your translations. Players move a character through a maze and eat letters to spell each one.
          </p>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold shadow disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>

      {/* Settings */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Game Settings</h3>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <Toggle k="is_visible" label="Show on Games list" hint="Turn off to hide Letter Eater from the games page." value={settings.is_visible} onChange={updateSetting} isDarkMode={isDarkMode} />
          <Toggle k="is_premium" label="Pro-only (whole game)" hint="Free users see an upgrade screen instead." value={settings.is_premium} onChange={updateSetting} isDarkMode={isDarkMode} />
        </div>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <TextField k="name" label="Game name" value={settings.name} onChange={updateSetting} isDarkMode={isDarkMode} />
          <TextField k="icon" label="Icon (emoji)" value={settings.icon} onChange={updateSetting} isDarkMode={isDarkMode} />
        </div>
        <div className="mb-3">
          <TextField k="description" label="Description (shown on game card)" value={settings.description} onChange={updateSetting} isDarkMode={isDarkMode} />
        </div>
        <div className="mb-3">
          <RadioGroup
            k="default_script"
            label="Default play mode"
            value={settings.default_script}
            onChange={updateSetting}
            isDarkMode={isDarkMode}
            options={[
              { value: 'latin', label: '🔤 Latin (salam)' },
              { value: 'arabic', label: 'ا Arabic (السلام)' },
            ]}
          />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <SelectField
            k="default_character"
            label="Default character"
            value={settings.default_character}
            onChange={updateSetting}
            isDarkMode={isDarkMode}
            options={CHARACTERS}
          />
          <SelectField
            k="default_theme"
            label="Default background theme"
            value={settings.default_theme}
            onChange={updateSetting}
            isDarkMode={isDarkMode}
            options={THEMES}
          />
        </div>
      </div>

      {/* Sentence picker */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Add Sentences from Translations</h3>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            placeholder="🔍 Search English word (e.g. hello, house, brother)…"
            className={inputCls + ' flex-1'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button onClick={suggestRandom} disabled={searching} className={smallBtnCls + ' whitespace-nowrap disabled:opacity-50'}>
            🎲 Suggest 30 random
          </button>
        </div>
        {searching && (
          <div className={`text-sm text-center py-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Searching…</div>
        )}
        {!searching && searchResults.length > 0 && (
          <div className="space-y-2 max-h-[28rem] overflow-y-auto">
            {searchResults.map((w) => (
              <div key={w.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{w.english}</div>
                  <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className="font-mono">{w.latin}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{ fontFamily: "'Noto Naskh Arabic', serif" }}>{w.arabic}</span>
                  </div>
                </div>
                {addedIds.has(w.id) ? (
                  <span
                    className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold ${
                      isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                    }`}
                    title="Already in Selected Sentences"
                  >
                    ✓ Added
                  </span>
                ) : (
                  <button
                    onClick={() => addSentence(w.id)}
                    disabled={addingId === w.id}
                    className={primaryBtnCls + ' flex-shrink-0 disabled:opacity-50'}
                  >
                    {addingId === w.id ? '…' : '+ Add'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {!searching && searchQuery && searchResults.length === 0 && (
          <div className={`text-sm text-center py-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No matching verified translations with both Latin + Arabic.
          </div>
        )}
      </div>

      {/* Selected sentences */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Selected Sentences ({sentences.length})</h3>
        {sentences.length === 0 ? (
          <div className={`text-sm text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            No sentences yet. Use the search above or "Suggest 30 random" to add some.
          </div>
        ) : (
          <div className="space-y-2">
            {sentences.map((s) => (
              <div key={s.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className="min-w-0 flex-1">
                  <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{s.english}</div>
                  <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <span className="font-mono">{s.latin}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{ fontFamily: "'Noto Naskh Arabic', serif" }}>{s.arabic}</span>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(s)} className={smallBtnCls}>✏</button>
                  <button onClick={() => askDelete(s)} className={dangerBtnCls}>🗑</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editSentence && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={cancelEdit}>
          <div className={`relative w-full max-w-md rounded-2xl shadow-2xl border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center gap-3 p-5 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <h3 className={`text-lg font-extrabold flex-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Edit sentence</h3>
              <button onClick={cancelEdit} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>×</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className={labelCls}>English</label>
                <input type="text" className={inputCls} value={editSentence.english} onChange={(e) => setEditSentence({ ...editSentence, english: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Latin script</label>
                <input type="text" className={inputCls + ' font-mono'} value={editSentence.latin} onChange={(e) => setEditSentence({ ...editSentence, latin: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Arabic script</label>
                <input type="text" className={inputCls} style={{ fontFamily: "'Noto Naskh Arabic', serif" }} value={editSentence.arabic} onChange={(e) => setEditSentence({ ...editSentence, arabic: e.target.value })} />
              </div>
            </div>
            <div className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${isDarkMode ? 'border-gray-800 bg-gray-900/60' : 'border-gray-100 bg-gray-50'} rounded-b-2xl`}>
              <button onClick={cancelEdit} className={`px-4 py-2 rounded-lg text-sm font-bold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}>Cancel</button>
              <button onClick={saveEdit} className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-bold">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setConfirmDelete(null)}>
          <div className={`relative w-full max-w-md rounded-2xl shadow-2xl border ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center gap-3 p-5 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
              <div className="w-10 h-10 rounded-full bg-red-500/15 text-red-500 flex items-center justify-center text-xl font-bold">⚠</div>
              <div className="flex-1">
                <h3 className={`text-lg font-extrabold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Remove this sentence?</h3>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Removes from Letter Eater only. Translation stays in your dictionary.</p>
              </div>
            </div>
            <div className="p-5">
              <div className={`p-4 rounded-lg border text-sm ${isDarkMode ? 'border-gray-700 bg-gray-800/40 text-gray-200' : 'border-gray-200 bg-gray-50 text-gray-800'}`}>
                <div className="font-semibold">{confirmDelete.english}</div>
                <div className="mt-1 text-xs opacity-80">
                  <span className="font-mono">{confirmDelete.latin}</span> · <span style={{ fontFamily: "'Noto Naskh Arabic', serif" }}>{confirmDelete.arabic}</span>
                </div>
              </div>
            </div>
            <div className={`flex items-center justify-end gap-2 px-5 py-4 border-t ${isDarkMode ? 'border-gray-800 bg-gray-900/60' : 'border-gray-100 bg-gray-50'} rounded-b-2xl`}>
              <button onClick={() => setConfirmDelete(null)} className={`px-4 py-2 rounded-lg text-sm font-bold ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}>Cancel</button>
              <button onClick={performDelete} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold">🗑 Remove</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LetterEaterAdmin;
